// Reading/writing localStorage or sessionStorage can throw SecurityError
// ("Access is denied for this document") instead of just being unavailable,
// specifically when this app runs inside a cross-origin iframe with storage
// access restricted (confirmed live 2026-07-27: the real Design It popup
// embed on search.promofit.nl, via CustomEdge_Connector's iframe overlay) —
// unlike most Web Storage docs/examples, which only mention it throwing when
// storage is full or disabled entirely.
//
// CRITICAL: the throw happens on the `localStorage`/`sessionStorage` PROPERTY
// ACCESS itself (`window.localStorage`), not on calling `.getItem()`/
// `.setItem()` on it — confirmed via the exact error text: "Failed to read
// the 'localStorage' property from 'Window'", not "Failed to execute
// 'getItem'". A first attempt at this fix passed `localStorage` as a plain
// function ARGUMENT to a try/catch-wrapped helper (`safeGetItem(localStorage,
// key)`) — that still crashed identically, because the argument expression
// `localStorage` is evaluated to build the call BEFORE the callee's own body
// (and its try/catch) ever runs. The access itself must be inside the try
// block, so these take a THUNK ('local' | 'session') and resolve
// window.localStorage/sessionStorage only inside the guarded try.

function resolveStorage(kind: 'local' | 'session'): Storage {
  return kind === 'local' ? window.localStorage : window.sessionStorage;
}

export function safeGetItem(kind: 'local' | 'session', key: string): string | null {
  try {
    return resolveStorage(kind).getItem(key);
  } catch {
    return null;
  }
}

export function safeSetItem(kind: 'local' | 'session', key: string, value: string): void {
  try {
    resolveStorage(kind).setItem(key, value);
  } catch {
    // Best-effort — a value that can't be persisted this session just won't
    // survive a reload; callers already handle a missing value as "not set".
  }
}

export function safeRemoveItem(kind: 'local' | 'session', key: string): void {
  try {
    resolveStorage(kind).removeItem(key);
  } catch {
    // No-op — nothing to clean up if storage was never reachable.
  }
}
